import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-63d160c2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "overlays" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = {
  key: 0,
  class: "backdrop"
}
const _hoisted_4 = {
  key: 0,
  class: "action"
}
const _hoisted_5 = { class: "title" }
const _hoisted_6 = { class: "title" }

import { computed, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import gsap from 'gsap'
import frames from '@/utils/FrameToTime'

import { useAppState, useGameState } from '@/services/composition'
import { HotspotId, getNextStepPosition } from '@/services/states/GameContextDatas'
import { Hotspot, useHotspots } from '@/services/stores/hotspots'

import CompanionPlayer from '@/components/CompanionPlayer.vue'
import CompanionHotspots from '@/components/CompanionHotspots/CompanionHotspots.vue'
import UIJourneyCurrentState, { NOTIFICATION_TIMEOUT } from '@/components/UIJourneyCurrentState/UIJourneyCurrentState.vue'
import UINavbar from '@/components/UINavbar/UINavbar.vue'
import UIGuide from '@/components/UIGuide/UIGuide.vue'
import UIEmojis from '@/components/UIEmojis/UIEmojis.vue';
import UIBannerInstructions from '@/components/UIBannerInstructions/UIBannerInstructions.vue';
import Delay from '@/core/Delay'


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const { t } = useI18n()
const state = useGameState()
const { send, state: appState } = useAppState()
const { hotspots, findHotspot } = useHotspots()

const isSync = computed(() => {
  return appState.value.matches('sync')
})

const isTuto = computed(() => {
   return state.value.matches('tuto.companion.contact') || state.value.matches('tuto.companion.plan') || state.value.matches('tuto.companion.teammate')
})

const currentHotspots = computed(()=> {
  if (isTuto.value) {
    return []
  }
  return hotspots.filter(hotspot => hotspot.states.some(hState => state.value.matches(hState))) as Hotspot[]
})

const showGuideTapHotspot = computed(() => {
  const advised = state.value.context.advisedHotspots
  const lastAdvisedHotspot = advised[advised.length - 1]
  const currentAdvisedHotspot = currentHotspots.value.find(hotspot => hotspot.id === lastAdvisedHotspot && !state.value.context.hotspots.includes(hotspot.id))
  return state.value.matches('tuto.companion.wait_teammate') || (state.value.matches('game') && !currentAdvisedHotspot)
  // return !currentAdvisedHotspot
})

const lastAdvisedHotspot = computed(() => {
  const advisedHotspots = state.value.context.advisedHotspots
  return advisedHotspots[advisedHotspots.length - 1]
})

const lastDoneHotspot = computed(() => {
  const id = state.value.context.hotspots[state.value.context.hotspots.length - 1] || -1
  return findHotspot(id)
})

watch(lastDoneHotspot, async (hotspot) => {
  if (hotspot.id === HotspotId.Step0_Door || hotspot.points > 0 || hotspot.points < 0) {
    showActionFeedback.value = true
    await Delay(NOTIFICATION_TIMEOUT)
    showActionFeedback.value = false
  }
})

const hotspotSelected = computed(() => {
  return state.value.context.hotspotSelected
})

watch(hotspotSelected, (id) => {
  showActionFeedback.value = false
})

/**
 * disable hotspots if last advised hotspot has not been clicked by the teammate yet
 */
const isLastAdvisedHotspotDone = computed(() => {
  const findIndex = currentHotspots.value.findIndex(hotspot => hotspot.id === lastAdvisedHotspot.value && !state.value.context.hotspots.includes(hotspot.id))
  return findIndex === -1
})

const showBannerInstructions = computed(() => {
  return !state.value.matches('tuto') && (!isLastAdvisedHotspotDone.value || showActionFeedback.value)
})

const waitForEscape = computed(() => {
  const isExit = state.value.matches('tuto.xr.exit_room') || state.value.matches('game.bedroom.exit_room') || state.value.matches('game.corridor.exit_room') || state.value.matches('game.studyroom.exit_room') || state.value.matches('game.living.exit_room')
  return isExit
})

const showActionFeedback = ref(false)

const bannerInstructionsKey = computed(() => {
  if (!isLastAdvisedHotspotDone.value) {
    return 'wait'
  } else if (lastDoneHotspot.value.id === HotspotId.Step0_Door || lastDoneHotspot.value.points > 0) {
    return 'success'
  } else if (lastDoneHotspot.value.points < 0) {
    return 'fail'
  }
  return 'wait'
})

const bannerInstructionsVariant = computed(() => {
  return bannerInstructionsKey.value
})

const bannerInstructionsAction = computed(() => {
  if (bannerInstructionsKey.value === 'success' || bannerInstructionsKey.value === 'fail') {
    return lastDoneHotspot.value && lastDoneHotspot.value.id && t(`hotspots.${lastDoneHotspot.value.name}.label`)
  }
  return null
})

const bannerInstructionsTitle = computed(() => {
  if (bannerInstructionsKey.value === 'success') {
    return t('game.companion.action_feedback.success')
  } else if (bannerInstructionsKey.value === 'fail') {
    return t('game.companion.action_feedback.fail')
  } else {
    return t('game.companion.wait')
  }
})

const onEnterBannerInstructions = (el: HTMLElement, onComplete: gsap.Callback) => {
  const $ = gsap.utils.selector(el)
  const tl = gsap.timeline({ onComplete })
  tl.fromTo(el, { yPercent: 100, }, {yPercent: 0, duration: frames(30), ease: 'quint.out' })
}

const onLeaveBannerInstructions = (el: HTMLElement, onComplete: gsap.Callback) => {
  const tl = gsap.timeline({ onComplete })

  tl.to(el, { yPercent: 100, duration: frames(30), ease: 'quint.in' })
}

const stateEvent = computed(()=>{
  return state?.value.event
})

watch(stateEvent, (stateEvent) => {
  if (stateEvent.type !== 'GAME_SET_COUNTDOWN') {
    console.log(stateEvent, state.value.value)
  }
  if (!isTuto.value && stateEvent.type === 'GAME_SEND_EMOJI') {
    updateEmojis()
  }
})

const emojis = ref([])
const updateEmojis = () => {
  if (state.value.context.emojis.length === emojis.value.length) return

  for (let i = 0; i < state.value.context.emojis.length - emojis.value.length; i++) {
    emojis.value.push(i)
  }
}

const isExit = computed(() => {
  return state.value.matches('game.bedroom.exit_room') || state.value.matches('game.corridor.exit_room') || state.value.matches('game.studyroom.exit_room') || state.value.matches('game.living.exit_room')
})

// watch(isExit, (isExit) => {
//   if(isExit) {
//     const position = getNextStepPosition(state.value)
//     send({ type: 'GAME_MOVE_PLAYER', payload: position})
//   }
// })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(CompanionPlayer),
    _createVNode(_Transition, { name: "medium-fade" }, {
      default: _withCtx(() => [
        (_unref(showGuideTapHotspot) && !_unref(waitForEscape) && !_unref(showBannerInstructions))
          ? (_openBlock(), _createBlock(UIGuide, { key: 0 }, {
              heading: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)('game.companion.tap_hotspot')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(CompanionHotspots),
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (showActionFeedback.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, {
      css: false,
      mode: "out-in",
      onEnter: onEnterBannerInstructions,
      onLeave: onLeaveBannerInstructions
    }, {
      default: _withCtx(() => [
        (_unref(showBannerInstructions))
          ? (_openBlock(), _createBlock(UIBannerInstructions, {
              key: _unref(bannerInstructionsKey),
              variant: _unref(bannerInstructionsVariant)
            }, {
              heading: _withCtx(() => [
                (_unref(bannerInstructionsAction))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(bannerInstructionsAction)), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(bannerInstructionsTitle)), 1)
              ]),
              _: 1
            }, 8, ["variant"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, {
      css: false,
      onEnter: onEnterBannerInstructions,
      onLeave: onLeaveBannerInstructions
    }, {
      default: _withCtx(() => [
        (_unref(waitForEscape))
          ? (_openBlock(), _createBlock(UIBannerInstructions, {
              key: 0,
              variant: 'wait'
            }, {
              heading: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)('game.companion.wait')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(UIEmojis, { emojis: emojis.value }, null, 8, ["emojis"]),
    _createVNode(UIJourneyCurrentState),
    _createVNode(UINavbar, {
      style: _normalizeStyle(_unref(isSync) && !_unref(state).context.isPaused ? 'z-index: 1;' : '')
    }, null, 8, ["style"])
  ]))
}
}

})